/* eslint-disable camelcase */
import { DateTime } from "luxon";
import { Tabulator, FormatModule, PageModule, SortModule, FrozenColumnsModule, InteractionModule } from "tabulator-tables";
import type { ColumnDefinition } from "tabulator-tables";

import type { Site } from "../../../api/site/structs";
import { installationSituationIdFormatter, obsNetworkIdFormatter, stringCellFormatter, workingTimeFormatter } from "../../../common/cellformatter";
import { pageUrls } from "../../../urls";

// 必要なモジュールを登録
Tabulator.registerModule([FormatModule, FrozenColumnsModule, PageModule, SortModule, InteractionModule]);

/**
 * 観測点検索→表エリア
 */
export class ResultArea {
    /** テーブルのルート要素 */
    #tableElem: HTMLElement;
    /** Tabulatorインスタンス */
    #table?: Tabulator;

    /**
     * コンストラクター。
     *
     * @param elem 表エリアのルート要素
     */
    constructor(elem: HTMLElement) {
        this.#tableElem = elem;
    }

    /**
     * 初期化処理を行う。
     * Tabulatorテーブルを構築し、行クリックイベントを設定する。
     */
    async init() {
        const table = new Tabulator(this.#tableElem, {
            dependencies: {
                DateTime,
            },
            minHeight: 400,
            layout: "fitDataTable",
            pagination: true,
            paginationSize: 20,
            paginationSizeSelector: [10, 20, 50, 100],
            paginationCounter: (pageSize: number, currentRow: number, _currentPage: number, totalRows: number) => {
                if (totalRows === 0) {
                    return "";
                }
                const startRow = currentRow;
                const endRow = Math.min(currentRow + pageSize - 1, totalRows);
                return `${startRow}-${endRow} 件目 / 全 ${totalRows} 件`;
            },
            columns: [
                { title: "観測点ID", field: "siteid2", frozen: true },
                {
                    title: "観測網",
                    field: "obsNetworkId",
                    frozen: true,
                    formatter: obsNetworkIdFormatter,
                },
                { title: "観測点コード", field: "siteCode", frozen: true },
                { title: "観測点名", field: "siteName" },
                {
                    title: "観測点運用開始日",
                    field: "startDate",
                    sorter: "datetime",
                    sorterParams: { format: "iso" },
                    formatter: workingTimeFormatter,
                },
                {
                    title: "観測点運用終了日",
                    field: "endDate",
                    sorter: "datetime",
                    sorterParams: { format: "iso" },
                    formatter: workingTimeFormatter,
                },
                { title: "緯度", field: "latStr", sorter: "number", hozAlign: "right", formatter: stringCellFormatter },
                { title: "経度", field: "lonStr", sorter: "number", hozAlign: "right", formatter: stringCellFormatter },
                { title: "標高 [m]", field: "elevationStr", sorter: "number", hozAlign: "right", formatter: stringCellFormatter },
                {
                    title: "設置場所",
                    field: "installationSituationId",
                    formatter: installationSituationIdFormatter,
                },
                { title: "AVS30 [m/s]", field: "avs30Str", sorter: "number", hozAlign: "right" },
            ] as ColumnDefinition[],
            locale: "ja-jp",
            langs: {
                "ja-jp": {
                    pagination: {
                        page_size: "ページサイズ",
                        first: "«",
                        first_title: "最初のページ",
                        last: "»",
                        last_title: "最後のページ",
                        prev: "‹",
                        prev_title: "前のページ",
                        next: "›",
                        next_title: "次のページ",
                    },
                },
            },
        });

        table.on("rowClick", (_, row) => {
            const siteid2 = row.getData().siteid2;

            if (typeof siteid2 === "string") {
                const url = pageUrls["/station/[siteid2]"]
                    .get({ params: { siteid2 } });
                window.open(url, "_blank", "noreferrer");
            }
        });

        this.#table = table;
    }

    /**
     * 観測点データをテーブルに表示する。
     *
     * @param sites 表示する観測点データの配列
     */
    async setSites(sites: Site[] | undefined) {
        const table = this.#table;
        if (!table) {
            console.warn("Table not initialized");
            return;
        }

        if (sites) {
            // データ設定後にカラム幅を自動調整
            await table.setData(sites);
            table.redraw(true);
        } else {
            table.clearData();
        }
    }
}
